/**
 * Sprint 5B — 3 gescheiden status-badges voor betalingsopvolging.
 * Nooit door elkaar gebruiken. Elk domein heeft eigen kleuren.
 */
import React from "react";
import {
  Play, Pause, Ban, Handshake, CheckCircle2, AlertOctagon,
  Send, Circle, Clock, AlertTriangle, XCircle, RotateCcw,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────
// Financial (blauw-familie)
// ─────────────────────────────────────────────────────────────
const FINANCIAL_CONFIG = {
  concept:       { label: "Concept",       cls: "bg-gray-100 text-gray-700 border-gray-300",         icon: Clock },
  verzonden:     { label: "Verzonden",     cls: "bg-sky-100 text-sky-800 border-sky-300",             icon: Send },
  open:          { label: "Open",          cls: "bg-blue-100 text-blue-800 border-blue-300",          icon: Circle },
  deels_betaald: { label: "Deels betaald", cls: "bg-amber-100 text-amber-800 border-amber-300",       icon: RotateCcw },
  betaald:       { label: "Betaald",       cls: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
  geannuleerd:   { label: "Geannuleerd",   cls: "bg-neutral-100 text-neutral-500 border-neutral-300 line-through", icon: XCircle },
  gecrediteerd:  { label: "Gecrediteerd",  cls: "bg-purple-100 text-purple-800 border-purple-300",    icon: RotateCcw },
};

export function FinancialStatusBadge({ status }) {
  const c = FINANCIAL_CONFIG[status] || {
    label: status || "onbekend",
    cls: "bg-gray-100 text-gray-600 border-gray-300",
    icon: Circle,
  };
  const Icon = c.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-bold uppercase tracking-wide rounded-none ${c.cls}`}
      data-testid={`financial-status-badge-${status || "unknown"}`}
      data-domain="financial"
    >
      <Icon className="w-3 h-3" />
      {c.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// Due (verkeerslicht: grijs/amber/rood)
// ─────────────────────────────────────────────────────────────
const DUE_CONFIG = {
  niet_van_toepassing: { label: "N.v.t.",             cls: "bg-white text-gray-500 border-gray-300",           icon: Circle },
  geen_vervaldatum:    { label: "Geen vervaldatum",   cls: "bg-white text-gray-500 border-gray-300 italic",    icon: Circle },
  niet_vervallen:      { label: "Niet vervallen",     cls: "bg-white text-slate-700 border-slate-400",         icon: Circle },
  vervalt_binnenkort:  { label: "Vervalt binnenkort", cls: "bg-white text-amber-700 border-amber-500",         icon: AlertTriangle },
  vervallen:           { label: "Vervallen",          cls: "bg-red-600 text-white border-red-700 font-bold",   icon: AlertOctagon },
};

export function DueStatusBadge({ status, daysOverdue }) {
  const c = DUE_CONFIG[status] || DUE_CONFIG.niet_vervallen;
  const Icon = c.icon;
  const showOverdueCount = status === "vervallen" && daysOverdue > 0;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-bold uppercase tracking-wide rounded-none ${c.cls}`}
      data-testid={`due-status-badge-${status || "unknown"}`}
      data-domain="due"
    >
      <Icon className="w-3 h-3" />
      {c.label}
      {showOverdueCount && ` (+${daysOverdue}d)`}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// Followup (icoon-first)
// ─────────────────────────────────────────────────────────────
const FOLLOWUP_CONFIG = {
  actief:            { label: "Actief",            cls: "bg-emerald-50 text-emerald-800 border-emerald-500", icon: Play },
  gepauzeerd:        { label: "Gepauzeerd",        cls: "bg-gray-100 text-gray-700 border-gray-400",         icon: Pause },
  hold:              { label: "Op hold",           cls: "bg-white text-red-700 border-red-500 border-dashed", icon: Ban },
  betalingsregeling: { label: "Betalingsregeling", cls: "bg-blue-50 text-blue-800 border-blue-500",          icon: Handshake },
  afgerond:          { label: "Afgerond",          cls: "bg-emerald-100 text-emerald-900 border-emerald-600", icon: CheckCircle2 },
  escalatie:         { label: "Escalatie",         cls: "bg-red-600 text-white border-red-800 font-bold",   icon: AlertOctagon },
};

export function FollowupStatusBadge({ status }) {
  const c = FOLLOWUP_CONFIG[status] || FOLLOWUP_CONFIG.actief;
  const Icon = c.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-bold uppercase tracking-wide rounded-none ${c.cls}`}
      data-testid={`followup-status-badge-${status || "unknown"}`}
      data-domain="followup"
    >
      <Icon className="w-3 h-3" />
      {c.label}
    </span>
  );
}

/**
 * Handige helper om alle 3 domein-badges op één regel te tonen.
 * (Wordt gebruikt in tabelrijen + factuurdetail-paneel.)
 */
export function TripleStatusBadges({ financial_status, due_status, days_overdue, followup_status }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid="triple-status-badges">
      <FinancialStatusBadge status={financial_status} />
      <DueStatusBadge status={due_status} daysOverdue={days_overdue} />
      <FollowupStatusBadge status={followup_status} />
    </div>
  );
}
